import axios, { AxiosInstance } from 'axios';
import keycloak from './keycloak';
import { ENV } from './env';

const API_VERSION = ENV.API_VERSION;
// Allow API_URL to be either a relative path (`/api`) for same-origin nginx
// proxying, or a full URL (`https://pmt-api.codespiresolutions.cloud/api`) for
// cross-origin direct calls to the backend.
const API_URL = ENV.API_URL || '/api';

// Lazily import the store to avoid circular dependency issues at module init time.
let _store: import('../app/store').AppStore | null = null;
export function injectStore(store: import('../app/store').AppStore) {
  _store = store;
}

export const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/${API_VERSION}`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor — attach Keycloak access token as Bearer header
api.interceptors.request.use(
  (config) => {
    const token = keycloak.token || (_store?.getState().auth.accessToken ?? null);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — on 401, ask Keycloak to refresh the token then retry
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Ask Keycloak to refresh if the token expires within 30 seconds
        const refreshed = await keycloak.updateToken(30);
        if (refreshed && keycloak.token && _store) {
          const { setKeycloakToken } = await import('../features/auth/authSlice');
          _store.dispatch(setKeycloakToken(keycloak.token));
        }
        if (keycloak.token) {
          originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed — Keycloak session ended, redirect to login
        keycloak.logout({ redirectUri: window.location.origin });
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
